import {
    Box,
    Skeleton,
    SortDirection,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { MouseEvent, ReactElement, memo } from "react"

import ApplicationRow from "./applicationRow"
import DnoApplicationListMobile from "./mobile/dnoApplicationListMobile"
import {
    ASC,
    DESC,
    ORDER_BY_TIMESTAMP,
} from "../../../constants/generalConstants"
import {
    TT_ADDRESS,
    TT_CAPACITY,
    TT_DEMAND,
    TT_INSTALLER,
    TT_POST_CODE,
    TT_OUTCOME_DNO,
    TT_LAST_UPDATED,
    TT_TECHNOLOGY,
    TT_STATUS_DNO,
} from "../../../constants/tooltips"
import { useScreenSize } from "../../../hooks/useScreenSize"
import { IApplicationSummary } from "../../../services/middleware/mappers/dnoInterface/dnoApplicationsMapper"
import ZeroDnoApplicationsMsg from "../../fallback/zeroDnoApplicationsMsg"
import AppTooltip from "../../helper/appTooltip"
import AppSkeleton from "../../loading/skeletons/appSkeleton"
import LctAvatar from "../../pills/lct/lctAvatar"
import AppPaper from "../../surfaces/appPaper"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

const avatarSx = { width: "40px", pl: 0.25 }
export const AvatarHeadingCell = (): ReactElement => (
    <AppTableCell sx={avatarSx} />
)

interface IAvatarContentCell {
    lcts: string[]
}
export const AvatarContentCell = ({
    lcts,
}: IAvatarContentCell): ReactElement => (
    <AppTableCell sx={avatarSx}>
        <LctAvatar lcts={lcts} />
    </AppTableCell>
)

interface IHeading {
    title: string
    tooltip: string
    sortId: string
    center: boolean
    enableSort: boolean
    showForEscalationPage: boolean
    showForApprovedPage: boolean
    xlOnly: boolean
    sortKey?: string
}

const HEADINGS: IHeading[] = [
    {
        title: "Address",
        tooltip: TT_ADDRESS,
        center: false,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Post Code",
        tooltip: TT_POST_CODE,
        center: false,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Installer",
        tooltip: TT_INSTALLER,
        center: false,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Last Updated",
        tooltip: TT_LAST_UPDATED,
        center: false,
        enableSort: true,
        sortId: ORDER_BY_TIMESTAMP,
        sortKey: "updatedSince",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Outcome",
        tooltip: TT_OUTCOME_DNO,
        center: true,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: false,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Status",
        tooltip: TT_STATUS_DNO,
        center: true,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: false,
        xlOnly: false,
    },
    {
        title: "Gen Capacity",
        tooltip: TT_CAPACITY,
        center: true,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: true,
    },
    {
        title: "Tot Demand",
        tooltip: TT_DEMAND,
        center: true,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: true,
    },
    {
        title: "Technology",
        tooltip: TT_TECHNOLOGY,
        center: false,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: false,
    },
    {
        title: "Assigned To",
        tooltip: "",
        center: true,
        enableSort: false,
        sortId: "1",
        showForEscalationPage: true,
        showForApprovedPage: true,
        xlOnly: true,
    },
]

// create array between 0 and 10

const escalationHeadings = HEADINGS.filter(
    (heading) => heading.showForEscalationPage
)

const approvedPageHeadings = HEADINGS.filter(
    (heading) => heading.showForApprovedPage
)

interface IApplicationTableProps {
    searchResults: IApplicationSummary[] | undefined
    orderBy: string
    orderDirection: string
    setOrderBy: (orderBy: string, orderDirection: string) => void
    isLoading: boolean
    isEscalations: boolean
    onSortChange: (sortKey: string) => void
}

/**
 * Displays listed applications. Enables ordering by clicking column headers
 */
const ApplicationTable = ({
    searchResults,
    orderBy,
    orderDirection,
    setOrderBy,
    isLoading,
    isEscalations,
    onSortChange,
}: IApplicationTableProps): ReactElement => {
    const handleRequestSort = (
        event_: MouseEvent<unknown>,
        property: keyof IApplicationSummary,
        sortKey: string
    ): void => {
        const isAsc = orderBy === property && orderDirection === ASC
        setOrderBy(sortKey, isAsc ? DESC : ASC)
        onSortChange(sortKey)
    }
    const createSortHandler =
        (property: keyof IApplicationSummary, sortKey: string) =>
        (event: MouseEvent<unknown>) => {
            handleRequestSort(event, property, sortKey)
        }
    /**
     * Returns flag denoting if no search results returned
     * @returns {boolean}
     */
    const emptySearch = (): boolean => {
        if (isLoading) {
            return false
        }
        if (Array.isArray(searchResults)) {
            return searchResults.length === 0
        }
        if (!searchResults) {
            return true
        }
        return false
    }

    const { isTabletOrMob } = useScreenSize()

    // Some headings should only be displayed on larger screens.
    // Note the component changes on md (tablet) breakpoint
    const { isXl } = useScreenSize()
    const headingsStage = isEscalations
        ? escalationHeadings
        : approvedPageHeadings

    const headings = isXl
        ? headingsStage
        : headingsStage.filter((heading) => !heading.xlOnly)

    // eslint-disable-next-line no-nested-ternary
    return emptySearch() ? (
        <AppPaper>
            <ZeroDnoApplicationsMsg escalatePage={isEscalations} />
        </AppPaper>
    ) : isTabletOrMob ? (
        <DnoApplicationListMobile
            searchResults={searchResults as IApplicationSummary[]}
            isEscalations={isEscalations}
            isLoading={isLoading}
        />
    ) : (
        <TableContainer>
            <Table size="medium">
                <TableHead>
                    <TableRow>
                        <AvatarHeadingCell />
                        {headings.map((heading: IHeading) => (
                            <AppTooltip
                                title={heading.tooltip}
                                key={heading.title}
                            >
                                <AppTableCell
                                    sortDirection={
                                        orderBy === heading.sortId
                                            ? (orderDirection as SortDirection)
                                            : false
                                    }
                                    align={heading.center ? "center" : "left"}
                                    sx={{
                                        cursor: heading.enableSort
                                            ? "pointer"
                                            : null,
                                    }}
                                >
                                    <TableSortLabel
                                        active={orderBy === heading.title}
                                        direction={
                                            orderBy === heading.sortId
                                                ? (orderDirection as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                                : (ASC as
                                                      | "asc"
                                                      | "desc"
                                                      | undefined)
                                        }
                                        onClick={createSortHandler(
                                            heading.sortId as keyof IApplicationSummary,
                                            heading.sortKey as string
                                        )}
                                        disabled={!heading.enableSort}
                                        sx={{
                                            "& .MuiTableSortLabel-icon": {
                                                opacity: 0,
                                            },
                                            "&:hover .MuiTableSortLabel-icon, &.Mui-active .MuiTableSortLabel-icon":
                                                {
                                                    opacity: 1,
                                                },
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight="medium"
                                            color="primary"
                                        >
                                            {heading.title}
                                        </Typography>
                                        {orderBy === heading.sortId ? (
                                            <Box
                                                component="span"
                                                sx={visuallyHidden}
                                            >
                                                {orderDirection === DESC
                                                    ? "sorted descending"
                                                    : "sorted ascending"}
                                            </Box>
                                        ) : null}
                                    </TableSortLabel>
                                </AppTableCell>
                            </AppTooltip>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {isLoading
                        ? Array.from(Array(2).keys()).map((v) => (
                              <AppTableContentRow
                                  hover
                                  key={`applicationTable${v}`}
                              >
                                  <AppTableCell>
                                      <Skeleton
                                          variant="circular"
                                          width={40}
                                          height={40}
                                      />
                                  </AppTableCell>
                                  {Array.from(
                                      Array(headings.length).keys()
                                  ).map((num) => (
                                      <AppTableCell key={num}>
                                          <AppSkeleton />
                                      </AppTableCell>
                                  ))}
                              </AppTableContentRow>
                          ))
                        : (searchResults as IApplicationSummary[]).map(
                              (result: IApplicationSummary) => (
                                  <ApplicationRow
                                      key={result.applicationId}
                                      application={result}
                                      isEscalations={isEscalations}
                                  />
                              )
                          )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default memo(ApplicationTable)
